import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError, formatINR } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Megaphone } from "lucide-react";

const STATUS_TONE = {
  draft: "bg-white/5 text-zinc-300 border-white/10",
  pending_review: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  approved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  running: "bg-[#D4B878]/15 text-[#E8D6A3] border-[#D4B878]/30",
  rejected: "bg-red-500/10 text-red-400 border-red-500/30",
  completed: "bg-zinc-500/10 text-zinc-300 border-zinc-500/20",
};

export default function AdReviewHQ() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    const { data } = await api.get("/ads/review");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const review = async (status) => {
    if (!selected) return;
    try {
      await api.patch(`/ads/campaigns/${selected.id}/review`, { status, notes });
      toast.success(`Marked ${status}`);
      setSelected(null);
      setNotes("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const pending = items.filter((i) => i.status === "pending_review");
  const running = items.filter((i) => i.status === "running");

  return (
    <div className="space-y-8" data-testid="ad-review-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
          <Megaphone size={12} className="inline mr-1" /> HQ · Ad review
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
          Moderate campaigns.
        </h1>
        <p className="text-zinc-400 mt-2">
          {pending.length} awaiting review · {running.length} running live.
        </p>
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-5 space-y-3">
          {items.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500">
              No campaigns.
            </div>
          )}
          {items.map((c) => (
            <button
              key={c.id}
              onClick={() => {
                setSelected(c);
                setNotes(c.review_notes || "");
              }}
              data-testid={`ad-review-row-${c.id}`}
              className={`w-full text-left rounded-2xl border p-4 transition flex items-center gap-4 ${
                selected?.id === c.id
                  ? "border-[#D4B878] bg-[#D4B878]/5"
                  : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
              }`}
            >
              <div className="w-16 h-12 rounded-lg overflow-hidden bg-black shrink-0 border border-white/10">
                <img src={c.creative_url} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <Badge variant="outline" className={`border ${STATUS_TONE[c.status] || ""} capitalize`}>
                    {c.status.replace("_", " ")}
                  </Badge>
                </div>
                <div className="font-semibold truncate">{c.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {c.advertiser_name} {c.advertiser_company ? `· ${c.advertiser_company}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-[#E8D6A3]">{formatINR(c.total_budget)}</div>
                <div className="text-[10px] text-zinc-500">{c.impressions} impr.</div>
              </div>
            </button>
          ))}
        </div>

        <div className="md:col-span-7">
          {!selected ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500">
              Select a campaign from the left to review.
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-5">
              <div className="rounded-xl overflow-hidden bg-black border border-white/10">
                {selected.creative_type === "video" ? (
                  <video src={selected.creative_url} controls className="w-full max-h-[360px] object-contain bg-black" />
                ) : (
                  <img src={selected.creative_url} alt="" className="w-full max-h-[360px] object-contain bg-black" />
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">
                  Campaign
                </div>
                <div className="text-2xl font-bold">{selected.name}</div>
                <div className="text-sm text-zinc-400 mt-1">
                  {selected.advertiser_name}
                  {selected.advertiser_company ? ` · ${selected.advertiser_company}` : ""}
                </div>
                <div className="text-xs text-zinc-500 mt-2">
                  {selected.start_date} → {selected.end_date} · Budget{" "}
                  {formatINR(selected.total_budget)} · Targets:{" "}
                  {selected.targeted_hub_ids?.length || "All hubs"}
                </div>
                {selected.click_url && (
                  <div className="text-xs text-zinc-500 mt-1 break-all">
                    → {selected.click_url}
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Review notes (optional)
                </div>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="ad-review-notes"
                  rows={3}
                  placeholder="Feedback for the advertiser…"
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => review("approved")}
                  data-testid="ad-review-approve"
                  className="bg-emerald-500 hover:bg-emerald-400 text-black rounded-full"
                >
                  <CheckCircle2 size={14} className="mr-2" /> Approve & run
                </Button>
                <Button
                  onClick={() => review("rejected")}
                  data-testid="ad-review-reject"
                  variant="outline"
                  className="bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20 rounded-full"
                >
                  <XCircle size={14} className="mr-2" /> Reject
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
